'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import NavBar from './sections/NavBar';
import MobileSidebar from './sections/MobileSidebar';
import FooterSection from './sections/FooterSection';
import { CallCenterWidgetLoader } from '@/components/callcenter-widget-loader';
import type { Lang, SectionVisibility, TranslationKeys } from './types';
import type { DynamicNavItem } from '@/lib/marketing/site-copy';

const ALL_SECTIONS: SectionVisibility = {
    showFeatures: true,
    showHowItWorks: true,
    showPricing: true,
    showFaq: true,
    showTrustBadges: true,
};

interface Props {
    children: React.ReactNode;
    // Server-resolved pair so this client component never imports the full
    // translations map. `lang` must match the URL locale prefix served by
    // proxy.ts; relying on navigator.language here used to cause a flash of
    // the default copy before the effect ran.
    lang: Lang;
    t: TranslationKeys;
    navTree?: DynamicNavItem[];
    /** When true, hide in-page section scroll links (for /[slug] etc) */
    hideSectionLinks?: boolean;
}

/**
 * Shared shell for landing pages: renders NavBar + Footer around children.
 * Used by /[slug] so all marketing pages keep the same header/footer.
 */
export default function LandingShell({ children, lang, t, navTree, hideSectionLinks }: Props) {
    const [langMenuOpen, setLangMenuOpen] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const router = useRouter();

    const handleLangChange = (newLang: Lang) => {
        if (typeof window === 'undefined') return;
        try {
            localStorage.setItem('lang', newLang);
            document.cookie = `ezdocu-locale=${newLang};path=/;max-age=${60 * 60 * 24 * 365};samesite=lax`;
        } catch { /* ignore cookie/storage write failures */ }
        setLangMenuOpen(false);
        const path = window.location.pathname.replace(/^\/(?:en|es|pt|de|fr)(?=\/|$)/, '');
        const target = path === '' ? `/${newLang}` : `/${newLang}${path}`;
        router.push(target, { scroll: false });
        router.refresh();
    };

    return (
        <>
            <NavBar
                t={t}
                lang={lang}
                sections={ALL_SECTIONS}
                langMenuOpen={langMenuOpen}
                setLangMenuOpen={setLangMenuOpen}
                onLangChange={handleLangChange}
                setMobileMenuOpen={setMobileMenuOpen}
                dynamicNav={navTree}
                hideSectionLinks={hideSectionLinks}
            />
            <MobileSidebar
                t={t}
                lang={lang}
                sections={ALL_SECTIONS}
                mobileMenuOpen={mobileMenuOpen}
                setMobileMenuOpen={setMobileMenuOpen}
                onLangChange={handleLangChange}
            />
            <main className="min-h-screen bg-white pt-28">
                {children}
            </main>
            <FooterSection t={t} />
            <CallCenterWidgetLoader locale={lang} />
        </>
    );
}
